import React from "react";
import { Link } from "react-router-dom";
import Icon from "./Icon";
import { COMPANION } from "../config/app";

/**
 * A contextual pointer to measurement, shown only where the page's own
 * numbers actually depend on a light assumption.
 *
 * The placement rule matters more than the copy. A sitewide banner gets
 * ignored; a line that says "this page assumes EV 15 and you're standing
 * somewhere else" is a reason. The engine already computes
 * `ndRequiredStops`, so the site knows exactly which pages have something
 * riding on the assumption and which don't — and this renders nothing on
 * the ones that don't.
 *
 * @param {object} props.recipe   - a generated recipe, optional
 * @param {object} props.lighting - the lighting condition, optional
 * @param {object} props.camera   - the camera, optional
 * @param {string} props.variant  - "inline" | "card"
 */
export default function MeasurePrompt({ recipe, lighting, camera, variant = "inline" }) {
  const settings = recipe ? recipe.settings : null;

  // Only worth showing where filtration is actually in play. A page whose
  // answer is "no ND needed, ISO 100, done" has nothing riding on whether
  // the light is half a stop off.
  const ndInPlay =
    settings && (settings.ndNeeded || settings.ndStatus === "recommended-unsupported");
  if (recipe && !ndInPlay) return null;

  const stops = settings ? settings.ndRequiredStops : null;
  const noFilter = camera && camera.capabilities && camera.capabilities.ndSupport === false;

  let body;
  if (noFilter && stops != null) {
    body = (
      <>
        You're roughly <strong className="data">{stops} stops</strong> over here and this camera has
        no filter thread, so knowing the real figure matters more than usual — it decides whether a
        clip-on is worth carrying or whether you shoot at a different hour.
      </>
    );
  } else if (stops != null) {
    body = (
      <>
        This page assumes {lighting ? lighting.name.toLowerCase() : "the selected condition"}
        {lighting && lighting.exposureStops != null && <> — about <span className="data">EV {lighting.exposureStops}</span></>}, which
        works out to <strong className="data">{stops} stops</strong> of filtration. Thin cloud or a
        bright wall moves that by a stop without looking like it has.
      </>
    );
  } else {
    body = (
      <>
        Filter choice depends on the light you're actually standing in, and a category picked from a
        list is an approximation of it.
      </>
    );
  }

  return (
    <aside className={`measure-prompt measure-prompt-${variant}`}>
      <Icon name="light" />
      <p>
        {body} <Link to="/light-meter">Measuring it with {COMPANION.name} →</Link>
      </p>
    </aside>
  );
}
